<template>
  <div class="edit-settings-template">
    <res-dialog :showDialog="showHelp"
                titleClass="resBlue darken-1 white--text"
                iconColor="white"
                cancelButtonText="Close"
                :cancelButtonOnly="true"
                cancelButtonColor="resBlue darken-1"
                :maxWidth="800"
                :setWidth="800"
                :setHeight="600"
                attach=".edit-settings-template"
                @cancel="closeHelp">
      <edit-budgets-help :openSection="openHelpSection"></edit-budgets-help>
    </res-dialog>
    <v-tabs v-model="tab"
            align-with-title>
      <v-tabs-slider color="yellow"></v-tabs-slider>
      <template v-for="(step, stepNum) in steps">
        <v-tab :key="stepNum">
          {{ step.name }}
        </v-tab>
      </template>
    </v-tabs>
    <v-tabs-items v-model="tab">
      <!-- General Settings -->
      <v-tab-item>
        <v-card class="mb-12"
                color="white"
                height="400px">

          <span class="step-title">{{steps[0].name}}</span>
          <br />
          <span class="banner">The settings template will only be used during the "Bulk Budget Creation" process.</span>
          <br />
          <v-row align-content="end">
            <v-col cols="9">
              <label>Settings Template Name</label>
              <res-text-field v-model="settingsTemplate.name"></res-text-field>
            </v-col>
            <v-col cols="3">
              <div class="help-controls step-down" @click="openHelp(1)">
                <span class="help-label">Help</span>
                <v-icon color="var(--v-resBlue-darken1)" class="pb-1">mdi-help-circle</v-icon>
              </div>
            </v-col>
          </v-row>
          <v-row>
            <v-col cols="6">
              <label>
                Estimate Future Historicals
                <v-tooltip top max-width="500px" nudge-right="50">
                  <ul>
                    <li>When viewing your Historical numbers, all of the months may not have numbers because the year may not be complete.</li>
                    <li>For Example: If you are working on your 2022 budget in September of 2021, then there will be no historical numbers for September, October, November or December of 2021 because these months have not happened.</li>
                    <li>These choices will offer you three options to populate those missing future months.</li>
                  </ul>
                  <template v-slot:activator="{ on, attrs }">
                    <div v-bind="attrs" v-on="on" class="inline">
                      <v-icon>info</v-icon>
                    </div>
                  </template>
                </v-tooltip>
              </label>
              <res-select :readonly="!canEdit" class="estimate-future-historicals-selector" v-model="settingsTemplate.estimateFutureHistoricalsOption" :items="estimateFutureHistoricalsOptions" :loading="currentLoading"></res-select>
            </v-col>
            <span v-if="this.settingsTemplate.estimateFutureHistoricalsOption != 5" class="step-down pt-5">and start in</span>
            <v-col v-if="this.settingsTemplate.estimateFutureHistoricalsOption != 5" cols="3" class="step-down pt-4">
              <res-select v-if="this.settingsTemplate.estimateFutureHistoricalsOption != 5" :readonly="!canEdit" class="estimate-future-historicals-month-selector" v-model="settingsTemplate.estimateFutureHistoricalOptionMonthOption" :items="estimateFutureHistoricalOptionMonthOptions" :loading="currentLoading" :menuProps="{top: true,nudgeBottom: '50px'}"></res-select>
            </v-col>
            <span v-if="this.settingsTemplate.estimateFutureHistoricalsOption != 5" class="step-down pt-5 pl-1">month</span>
            <div v-if="this.settingsTemplate.estimateFutureHistoricalsOption == 5"></div>
          </v-row>
          <v-row>
            <v-col cols="6">
              <label>Approval Workflow</label>
              <res-select :readonly="!canEdit" v-model="settingsTemplate.approvalWorkflowId" :items="approvalworkflowOptions"></res-select>
            </v-col>
          </v-row>
        </v-card>

        <v-btn v-if="settingsTemplateId"
               color="primary"
               :disabled="currentLoading || readonly || !steps[0].isComplete(settingsTemplate)"
               @click="save">
          Save
        </v-btn>
      </v-tab-item>

      <!-- Lease Calculations -->
      <v-tab-item>

        <v-card class="mb-12"
                color="white"
                height="100%">

          <span class="step-title">{{steps[1].name}}</span>
          <br />
          <span class="banner">The settings template will only be used during the "Bulk Budget Creation" process.</span>
          <br />
          <v-row>
            <v-col cols="10">
              <label>New Lease Calculations</label>
              <res-select :readonly="!canEdit" class="new-lease-calculations-selector" v-model="settingsTemplate.newLeaseCalculationOption" :items="newLeaseCalculationOptions" :loading="currentLoading"></res-select>
            </v-col>
            <v-col cols="2">
              <div class="help-controls step-down" @click="openHelp(2)">
                <span class="help-label">Help</span>
                <v-icon color="var(--v-resBlue-darken1)" class="pb-1">mdi-help-circle</v-icon>
              </div>
            </v-col>
          </v-row>

          <v-row v-if="settingsTemplate.newLeaseCalculationOption==1" class="pl-3 mt-0">
            <span class="pt-5">The discount will be</span>
            <v-col cols="1">
              <res-text-field :readonly="!canEdit" v-model="settingsTemplate.discountMarketRentPercentage"></res-text-field>
            </v-col>
            <span class="pt-5">% of Market Rent.</span>
          </v-row>

          <v-row v-if="settingsTemplate.newLeaseCalculationOption==3" class="pl-3 mt-0">
            <span class="pt-5">This recurring concession will be</span>
            <v-col cols="1">
              <res-text-field :readonly="!canEdit" v-model="settingsTemplate.newRecurringConcessionMarketRentPercentage"></res-text-field>
            </v-col>
            <span class="pt-5">% of Market Rent and will use concession transaction category:</span>
            <v-col cols="12" class="pl-0 px-6 pt-0">
              <v-combobox :readonly="!canEdit" ref="newRecurringConcessionCombo" v-model="settingsTemplate.newRecurringConcessionTransactionCategoryId" :items="transactionCategories" outlined dense flat height="12"></v-combobox>
            </v-col>
          </v-row>

          <v-row>
            <v-col cols="12">
              <label>Renewal Pricing Calculations</label>
              <res-select :readonly="!canEdit" class="renewal-pricing-calculations-selector" v-model="settingsTemplate.renewalPricingCalculationOption" :items="renewalPricingCalculationOptions" :loading="currentLoading"></res-select>
            </v-col>
          </v-row>

          <v-row v-if="settingsTemplate.renewalPricingCalculationOption==1" class="pl-3 mt-0">
            <span class="pt-5">This increase will be</span>
            <v-col cols="1">
              <res-text-field class="short" :readonly="!canEdit" v-model="settingsTemplate.increaseOverExistingRentAmount"></res-text-field>
            </v-col>
            <v-col cols="2">
              <res-select :readonly="!canEdit" v-model="settingsTemplate.increaseOverExistingRentDenomination" :items="denominationOptions"></res-select>
            </v-col>
            <span class="pt-5">over existing rent.</span>
          </v-row>

          <v-row v-if="settingsTemplate.renewalPricingCalculationOption==3" class="pl-3 mt-0">
            <span class="pt-5">This recurring concession will be</span>
            <v-col cols="1">
              <res-text-field :readonly="!canEdit" v-model="settingsTemplate.renewalRecurringConcessionMarketRentPercentage"></res-text-field>
            </v-col>
            <span class="pt-5">% of Market Rent and will use concession transaction category:</span>
            <v-col cols="12" class="pl-0 px-6 pt-0">
              <v-combobox :readonly="!canEdit" ref="rewewalRecurringConcessionCombo" v-model="settingsTemplate.renewalRecurringConcessionTransactionCategoryId" :items="transactionCategories" outlined dense flat height="12"></v-combobox>
            </v-col>
          </v-row>
        </v-card>

        <v-btn v-if="settingsTemplateId"
               color="primary"
               :disabled="currentLoading || readonly || !steps[1].isComplete(settingsTemplate)"
               @click="save">
          Save
        </v-btn>
      </v-tab-item>

      <!-- Other Concessions and Bad Debt -->
      <v-tab-item>
        <v-card class="mb-12 scrollable-card"
                color="white"
                height="420px">

          <span class="step-title">{{steps[2].name}}</span>
          <br />
          <span class="banner">The settings template will only be used during the "Bulk Budget Creation" process.</span>
          <br />
          <v-row>
            <v-col cols="9" class="py-0">
              <label>Other Concessions (Optional)</label>
            </v-col>
            <v-col cols="3" class="py-0">
              <div class="help-controls" @click="openHelp(3)">
                <span class="help-label">Help</span>
                <v-icon color="var(--v-resBlue-darken1)" class="pb-1">mdi-help-circle</v-icon>
              </div>
            </v-col>
            <v-col cols="12" class="py-0">
              <span>Want to enter budget numbers for additional concessions?  If so, please select them below:</span>
            </v-col>
            <v-col cols="12" class="py-0">
              <v-combobox :readonly="!canEdit" ref="otherConcessionTransactionCategoriesCombo" color="resBlue darken-1" dense outlined class="py-0" multiple v-model="settingsTemplate.otherConcessionTransactionCategories" :items="transactionCategories"></v-combobox>
            </v-col>
          </v-row>

          <v-row class="py-0 mt-0">
            <v-col cols="10" class="py-0">
              <label>Bad Debt</label>
              <res-select :readonly="!canEdit" v-model="settingsTemplate.badDebtOption" :items="badDebtOptions"></res-select>
            </v-col>
          </v-row>

          <v-row v-if="settingsTemplate.badDebtOption==1" class="pl-3 py-0">
            <span class="pt-5">This will be</span>
            <v-col cols="1">
              <res-text-field :readonly="!canEdit" v-model="settingsTemplate.badDebtGLPercentage"></res-text-field>
            </v-col>
            <span class="pt-5">% of</span>
            <v-col cols="8">
              <v-combobox :readonly="!canEdit" ref="glCombo" color="resBlue darken-1" v-model="settingsTemplate.badDebtGLAccount" :items="glAccounts" :filter="(item, queryText, itemText) => isNaN(queryText) ? itemText.toLocaleLowerCase().indexOf(queryText.toLocaleLowerCase()) !== -1 : itemText.toLocaleLowerCase().startsWith(queryText.toLocaleLowerCase())" outlined dense flat height="12"></v-combobox>
            </v-col>
          </v-row>

          <br v-if="settingsTemplate.badDebtOption!==1" />
          <v-row class="py-0 mt-0">
            <v-col cols="10" class="py-0">
              <span>Bad Debt Transaction Category is: </span>
            </v-col>
            <v-col cols="10" class="py-0">
              <v-combobox :readonly="!canEdit" ref="badDebtCombo" color="resBlue darken-1" v-model="settingsTemplate.badDebtTransactionCategoryId" :items="transactionCategories" outlined dense flat height="12"></v-combobox>
            </v-col>
          </v-row>

        </v-card>

        <v-btn v-if="settingsTemplateId"
               color="primary"
               :disabled="currentLoading || readonly || !steps[2].isComplete(settingsTemplate)"
               @click="save">
          Save
        </v-btn>
      </v-tab-item>
    </v-tabs-items>
  </div>
</template>

<script>
  import ResSelect from '@/components/base/ResSelect.vue';
  import ResTextField from '@/components/base/ResTextField.vue';
  import ResDialog from '@/components/base/ResDialog.vue';

  import IsNullOrWhitespaceMixin from '@/mixins/isNullOrWhitespace.js';

  import EditBudgetsHelp from '@/components/help/EditBudgetsHelp.vue';
  import HelpableMixin from '@/mixins/Helpable.js';

  import accountService from '@/services/account';
  import settingsTemplateService from '@/services/settings-template';
  import glAccountService from '@/services/gl-account';
  import workflowService from '@/services/workflow';

  import { mapActions } from 'vuex';
  import { mapGetters } from 'vuex';

  export default {
    props: {
      height: {
        type: Number
      },
      maxWidth: {
        type: Number
      },
      showDialog: {
        type: Boolean
      },
      width: {
        type: Number
      },
      settingsTemplateId: {
        type: String,
        default: null
      }
    },
    mixins: [IsNullOrWhitespaceMixin, HelpableMixin],
    data() {
      return {
        tab: 0,
        currentStep: 1,
        steps: [
          {
            name: 'General Settings',
            isComplete: (settingsTemplate) => { return Boolean(settingsTemplate.name && settingsTemplate.name.length > 2 && settingsTemplate.estimateFutureHistoricalsOption && settingsTemplate.estimateFutureHistoricalOptionMonthOption); }
          },
          {
            name: 'Lease Calculations',
            isComplete: (settingsTemplate) => {
              return Boolean(settingsTemplate.newLeaseCalculationOption > 0 &&
                (settingsTemplate.newLeaseCalculationOption == 1 ? settingsTemplate.discountMarketRentPercentage > 0 : true) &&
                (settingsTemplate.newLeaseCalculationOption == 3 ? settingsTemplate.newRecurringConcessionMarketRentPercentage && settingsTemplate.newRecurringConcessionTransactionCategoryId : true))
                &&
                Boolean(settingsTemplate.renewalPricingCalculationOption > 0 &&
                  (settingsTemplate.renewalPricingCalculationOption == 1 ? settingsTemplate.increaseOverExistingRentAmount > 0 && settingsTemplate.increaseOverExistingRentDenomination > 0 : true) &&
                  (settingsTemplate.renewalPricingCalculationOption == 3 ? settingsTemplate.renewalRecurringConcessionMarketRentPercentage && settingsTemplate.renewalRecurringConcessionMarketRentPercentage && settingsTemplate.renewalRecurringConcessionTransactionCategoryId : true))
            }
          },
          {
            name: 'Other Concessions and Bad Debt',
            isComplete: (settingsTemplate) => {
              return Boolean(settingsTemplate.badDebtOption &&
                (settingsTemplate.badDebtOption > 1 || settingsTemplate.badDebtGLPercentage && settingsTemplate.badDebtGLAccount && settingsTemplate.badDebtGLAccount.value) &&
                settingsTemplate.badDebtTransactionCategoryId)
            }
          }
        ],
        transactionCategories: [],
        approvalworkflowOptions: [],
        glAccounts: [],
        lastModifiedTimestamp: null,
        rawSettingsTemplate: null,
        settingsTemplate: {
          property: { text: '', value: '' },
          year: null,
          accountingBasisId: 1,
          startMonth: '',
          name: 'New Settings Template',
          template: '',
          estimateFutureHistoricalsOption: 0,
          estimateFutureHistoricalOptionMonthOption: 0,
          newLeaseCalculationOption: 1,
          renewalPricingCalculationOption: 1,
          newRecurringConcessionMarketRentPercentage: 0,
          renewalRecurringConcessionMarketRentPercentage: 0,
          newRecurringConcessionTransactionCategoryId: null,
          renewalRecurringConcessionTransactionCategoryId: null,
          badDebtOption: 1,
          badDebtTransactionCategoryId: null,
          beginningVacancies: [],
          approvalWorkflowId: null
        }
      }
    },
    components: {
      ResSelect,
      ResTextField,
      ResDialog,
      EditBudgetsHelp
    },
    methods: {
      ...mapActions(['changeLoading']),
     getMonthOptions(year, includeYear, startMonth) {
        startMonth = startMonth || 0;
        let priorYear = includeYear ? year - 1 : '';
        if (priorYear > 0 || !includeYear) {
          var months = [
            { text: 'January ', value: 1 },
            { text: 'February ', value: 2 },
            { text: 'March ', value: 3 },
            { text: 'April ', value: 4 },
            { text: 'May ', value: 5 },
            { text: 'June ', value: 6 },
            { text: 'July ', value: 7 },
            { text: 'August ', value: 8 },
            { text: 'September ', value: 9 },
            { text: 'October ', value: 10 },
            { text: 'November ', value: 11 },
            { text: 'December ', value: 12 }
          ];

          var returnMonths = [];
          for (var i = startMonth; i < startMonth + 12; i++) {
            var thisMonth = months[i % 12];
            returnMonths.push(thisMonth)
          }
          return returnMonths;
        }
        return []
      },
      async getConcessionTransactionCategories() {
        var result = await accountService.getTransactionCategories('Credit');

        this.transactionCategories = result
          .filter((cat) => { return !cat.isGLAccountGainToLeaseLedgerItem && !cat.isUnitStatusVacancyLossLedgerItem })
          .map((cat) => { return { text: cat.name, value: cat.ledgerItemTypeId }; })
          .sort((a, b) => (a && a.text || '').toLowerCase().localeCompare((b && b.text || '').toLowerCase()));
      },
      async loadAvailableWorkflows() {
        let workflowData = await workflowService.getDropdown();
        if (workflowData && workflowData.data) {
          let wfs = [];

          wfs.push({ text: 'No approval workflow selected', value: 0 });
          for (var i = 0; i < workflowData.data.length; i++) {
            wfs.push(workflowData.data[i]);
          }
          this.approvalworkflowOptions = wfs;
        } else {
          let wfs = [];
          wfs.push({ text: 'No approval workflow selected', value: 0 });
          this.approvalworkflowOptions = wfs;
        }
      },
      async loadGlAccounts() {
        this.glAccounts = ((await glAccountService.getGlAccounts(null, 'Income')).concat(await glAccountService.getGlAccounts(null, 'Expense')))
          .sort((glA, glB) => { if (glA.number < glB.number) { return -1; } if (glA.number > glB.number) { return 1; } return 0; })
          .map((gl) => { return { text: gl.number + ' - ' + gl.name, value: gl.glAccountId }; });
      },
      async loadSettingsTemplate() {
        if (!this.settingsTemplateId) {
          return;
        }
        var settingsTemplate = await settingsTemplateService.get(this.settingsTemplateId);
        this.settingsTemplate = {
          name: settingsTemplate.name,
          increaseOverExistingRentAmount: settingsTemplate.increaseOverExistingRentAmount,
          increaseOverExistingRentDenomination: settingsTemplate.increaseOverExistingRentDenomination,
          discountMarketRentPercentage: settingsTemplate.discountMarketRentPercentage,
          estimateFutureHistoricalsOption: settingsTemplate.estimateFutureHistoricalsOption,
          estimateFutureHistoricalOptionMonthOption: settingsTemplate.estimateFutureHistoricalOptionMonthOption,
          newLeaseCalculationOption: settingsTemplate.newLeaseCalculationOption,
          renewalPricingCalculationOption: settingsTemplate.renewalPricingCalculationOption,
          newRecurringConcessionMarketRentPercentage: settingsTemplate.newRecurringConcessionMarketRentPercentage,
          renewalRecurringConcessionMarketRentPercentage: settingsTemplate.renewalRecurringConcessionMarketRentPercentage,
          badDebtOption: settingsTemplate.badDebtOption,
          badDebtGLPercentage: settingsTemplate.badDebtGLPercentage,
          badDebtTransactionCategoryId: settingsTemplate.badDebtTransactionCategoryId,
          approvalWorkflowId: settingsTemplate.approvalWorkflowId
        };

        this.rawSettingsTemplate = settingsTemplate;
        this.updateControls(settingsTemplate);
      },
      updateControls(settingsTemplate) {
        if (!settingsTemplate) {
          return;
        }
        setTimeout(() => {
          this.settingsTemplate.approvalWorkflowId = settingsTemplate.approvalWorkflowId || 0;
          if (this.transactionCategories.length > 0) {
            let fullOtherConcessionTransactionCategories = this.settingsTemplate.otherConcessionTransactionCategories || [];
            this.transactionCategories.forEach((cat) => {
              if (cat.value == settingsTemplate.badDebtTransactionCategoryId) {
                this.settingsTemplate.badDebtTransactionCategoryId = cat;
                if (this.$refs.badDebtCombo) {
                  this.$refs.badDebtCombo.selectItem(cat);
                }
              }
              if (cat.value == settingsTemplate.newRecurringConcessionTransactionCategoryId) {
                this.settingsTemplate.newRecurringConcessionTransactionCategoryId = cat;
              }
              if (cat.value == settingsTemplate.renewalRecurringConcessionTransactionCategoryId) {
                this.settingsTemplate.renewalRecurringConcessionTransactionCategoryId = cat;
              }

              var index = settingsTemplate.otherConcessionTransactionCategories.indexOf(cat.value);
              if (index !== -1) {
                fullOtherConcessionTransactionCategories.push(cat);
                settingsTemplate.otherConcessionTransactionCategories.splice(index, 1)
              }
            })
            this.settingsTemplate.otherConcessionTransactionCategories = fullOtherConcessionTransactionCategories;
          }
          if (this.glAccounts.length > 0) {
            this.glAccounts.forEach((gl) => {
              if (gl.value == settingsTemplate.badDebtGLAccountId) {
                this.settingsTemplate.badDebtGLAccount = gl;
                if (this.$refs.glCombo) {
                  this.$refs.glCombo.selectItem(gl);
                }
              }
            });
          }
        }, 2500);
        this.lastModifiedTimestamp = this.settingsTemplate.lastModifiedTimestamp = settingsTemplate.lastModifiedTimestamp;
      },
      async save() {
        this.changeLoading(true);
        let succeeded = false;

        succeeded = await settingsTemplateService.update(this.settingsTemplateId, this.settingsTemplate);

        this.$emit('save', succeeded);
        if (succeeded) {
          this.$eventHub.$emit('SettingsTemplateUpdated', this.settingsTemplateId);
          this.$emit('settingsTemplateUpdated');
          this.$eventHub.$emit('showAlertNotification', 'Settings template was updated successfully!');
        }
        this.changeLoading(false);
      },
      cancel() {
        this.$emit('cancel', true)
      }
    },
    computed: {
      ...mapGetters(['currentLoading']),
      readonly() {
         return false;
      },
      startMonthOptions() {
        return this.getMonthOptions(0, true)
      },
      accountingBasiiOptions() {
        return [
          { text: 'Accrual', value: 1 },
          { text: 'Cash', value: 2 }
        ]
      },
      estimateFutureHistoricalsOptions() {
        return [
          { text: `Estimate Future Historicals by annualizing current year actuals`, value: 1 },
          { text: `Estimate Future Historicals by using prior year actuals`, value: 2 },
          { text: `Estimate Future Historicals using current year budget`, value: 3 },
          { text: `Estimate Future Historicals by using current year reforecast`, value: 4 },
          { text: `Do not Estimate Future Historical months `, value: 5 }
        ]
      },
      estimateFutureHistoricalOptionMonthOptions() {
        var options = []
        switch (this.settingsTemplate.estimateFutureHistoricalsOption) {
          case 1:
            options = this.getMonthOptions(0, false, 0);
            break;
          case 2:
            options = this.getMonthOptions(0, false, 0);
            break;
          case 3:
            options = this.getMonthOptions(0, false, 0);
            break;
          case 4:
            options = this.getMonthOptions(0, false, 0);
            break;
          case 5:
            options = this.getMonthOptions(0, false, 0);
            break;
        }
        return options;
      },
      newLeaseCalculationOptions() {
        return [
          { text: 'New Leases will be given a percentage discount off of Market Rent (yes Gain/Loss to Lease)', value: 1 },
          { text: 'New Leases will be at Market Rent (no Gain/Loss to Lease)', value: 2 },
          { text: 'New Leases will be at Market Rent AND also offer a recurring concession (no Gain/Loss to Lease)', value: 3 }
        ]
      },
      renewalPricingCalculationOptions() {
        return [
          { text: 'Renewals will be given an increase over existing Rent', value: 1 },
          { text: 'Renewals will be at Market Rent', value: 2 },
          { text: 'Renewals will be at Market Rent AND also offer a recurring concession', value: 3 }
        ]
      },
      badDebtOptions() {
        return [
          { text: 'Bad Debt will be a percentage of the selected GL Account', value: 1 },
          { text: 'Bad Debt will be Average Market Rent multiplied by the number of skips/evictions', value: 2 },
          { text: 'Manual Entry', value: 3 }
        ]
      },
      denominationOptions() {
        return [
          { text: 'dollars ($)', value: 1 },
          { text: 'percent (%)', value: 2 }
        ];
      },
      canEdit() {
        return !this.readonly && this.settingsTemplateId;
      }
    },
    async mounted() {
      this.loadGlAccounts();
      await this.loadAvailableWorkflows();
      this.loadSettingsTemplate();
      await this.getConcessionTransactionCategories();
    },
    watch: {
      'tab': function () {
        if (this.rawSettingsTemplate) {
          this.updateControls(this.rawSettingsTemplate);
        }
      },
      'estimateFutureHistoricalOptionMonthOptions': function (options) {
        setTimeout(() => {
          var currentMonthOption = options.filter((m) => m.value == (new Date).getMonth() + 1)[0];
          this.settingsTemplate.estimateFutureHistoricalOptionMonthOption = this.settingsTemplate.estimateFutureHistoricalOptionMonthOption || (currentMonthOption && currentMonthOption.value);
        });
      }
    }
  };
</script>

<style lang="scss" scoped>
  .v-stepper,
  .v-stepper__header,
  .v-card.v-sheet {
    box-shadow: none;
  }

  .inline {
    display: inline !important;
  }

  .step-title {
    text-align: center;
    font-size: 24px;
    padding-bottom: 0;
    font-weight: bold;
    display: block;
  }

  .banner {
    text-align: center;
    background-color: var(--v-resGrey-lighten1);
    display: block;
  }

  .step-down {
    position: relative;
    top: 18px;
  }

  .resGrey--border {
    border: 1px solid var(--v-resGrey-darken1) !important;
    padding: 8px;
  }

  .help-controls {
    float: right;
    border-radius: 5px;
    border: solid 1px var(--v-resGrey-lighten3);
    padding: 7px 6px 6px 6px;
    cursor: pointer !important;
  }
</style>

<style>
  div[role="combobox"] .v-text-field.v-text-field--solo .v-input__control {
    min-height: 20px;
  }

  div[role="combobox"] .v-input__append-inner {
    background-color: var(--v-resBlue-darken1);
    margin-top: 0 !important;
    margin-right: 0;
    position: absolute;
    right: -13px;
    border-bottom-right-radius: 5px;
    border-top-right-radius: 5px;
  }

  div[role="combobox"] .v-input__append-inner {
    padding-bottom: 7px !important;
  }

  div[role="combobox"] .v-input__icon--append {
    margin-top: -3px;
  }

  div[role="combobox"] .v-input__append-inner .v-icon {
    color: white !important;
  }

  .v-input--is-readonly div[role="combobox"],
  .v-input--is-readonly input[readonly="readonly"],
  .v-input--is-readonly .v-select__slot {
    opacity: 0.65;
  }

  div[role="combobox"] .v-input__append-inner {
    height: 40px !important;
  }

  div[role="combobox"] .v-input__icon.v-input__icon--append {
    margin-top: 8px;
    margin-left: 3px;
    padding-right: 5px;
  }
</style>
